import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H3,
  H6,
} from "@sellar-io/components";
import { SupplierRelationshipDiscountListResponseType } from "@sellar-io/api-v2/types";
import { pluralize } from "@sellar-io/common";
import { useState } from "react";
import { Icon, SpinnerLegacy } from "../../../components";
import {
  TableLegacy,
  TableLegacyContainer,
  TableLegacyTHead,
  TableLegacyTR,
  TableLegacyTH,
  TableLegacyTBody,
  TableLegacyTD,
} from "../../../components/table/TableLegacy";
import { useSupplierRelationshipDiscountList } from "../../../hooks/api/v2/supplierRelationshipDiscount/useSupplierRelationshipDiscountList";
import { formatPriceApiToInput } from "../../../hooks/usePriceFormat";
import { BusinessConnectionDiscountAddDialog } from "./BusinessConnectionDiscountAddDialog";
import { BusinessConnectionDiscountUpdateDialog } from "./BusinessConnectionDiscountUpdateDialog";

const generateConnectionDiscountDescription = (
  connectionDiscount: SupplierRelationshipDiscountListResponseType[number],
) => {
  if (connectionDiscount.type === "percentage") {
    return [
      `Apply ${connectionDiscount.discountAmount}% discount`,
      connectionDiscount.itemType !== "any" &&
        `to ${pluralize(connectionDiscount.itemType, 2)}`,
    ]
      .filter(Boolean)
      .join(" ");
  }

  const discountAmountAsPrice = formatPriceApiToInput(
    connectionDiscount.discountAmount ?? 0,
  );

  return [
    `Apply £${discountAmountAsPrice} discount`,
    connectionDiscount.itemType !== "any" &&
      `to ${pluralize(connectionDiscount.itemType, 2)}`,
  ]
    .filter(Boolean)
    .join(" ");
};

export const BusinessConnectionDiscountsCard = ({
  connectionId,
}: {
  connectionId: number;
}) => {
  const [isModalActive, setIsModalActive] = useState(false);

  const connectionDiscountListQuery = useSupplierRelationshipDiscountList({
    query: [
      {
        type: "SupplierRetailerConnectionId",
        modifier: "EQUALS",
        value: connectionId,
      },
    ],
  });

  return (
    <Card className="bg-white">
      <CardHeader
        className={twMerge("bg-white", "flex items-center justify-between")}
      >
        <div>
          <H3>Discounts by container type</H3>
          <H6>
            {connectionDiscountListQuery.data?.pages[0]?.length ?? "0"}{" "}
            discounts applied
          </H6>
        </div>

        <Button color={"secondary"} onClick={() => setIsModalActive(true)}>
          <Icon icon={"PLUS"} />
          Add discount
        </Button>
      </CardHeader>
      <CardContent>
        {connectionDiscountListQuery.isPending ? (
          <SpinnerLegacy />
        ) : connectionDiscountListQuery.isError ? (
          <div>Something went wrong</div>
        ) : (
          <TableLegacyContainer>
            <TableLegacy>
              <TableLegacyTHead>
                <TableLegacyTR>
                  <TableLegacyTH>Discount rule</TableLegacyTH>
                  <TableLegacyTH></TableLegacyTH>
                </TableLegacyTR>
              </TableLegacyTHead>
              <TableLegacyTBody>
                {connectionDiscountListQuery.data.pages.map((page) =>
                  page.map((discount) => (
                    <DiscountRow key={discount.id} discount={discount} />
                  )),
                )}
              </TableLegacyTBody>
            </TableLegacy>
          </TableLegacyContainer>
        )}
      </CardContent>
      <BusinessConnectionDiscountAddDialog
        connectionId={connectionId}
        active={isModalActive}
        onDismiss={() => setIsModalActive(false)}
      />
    </Card>
  );
};

const DiscountRow = ({
  discount,
}: {
  discount: SupplierRelationshipDiscountListResponseType[number];
}) => {
  const [isModalActive, setIsModalActive] = useState(false);

  return (
    <TableLegacyTR key={discount.id}>
      <TableLegacyTD>
        {generateConnectionDiscountDescription(discount)}
      </TableLegacyTD>
      <TableLegacyTD className={"flex place-content-end"}>
        <Button color={"transparent"} onClick={() => setIsModalActive(true)}>
          <Icon icon={"PENCIL"} />
        </Button>
      </TableLegacyTD>
      <BusinessConnectionDiscountUpdateDialog
        editingConnectionDiscount={discount}
        active={isModalActive}
        onDismiss={() => setIsModalActive(false)}
        connectionId={discount.SupplierRetailerConnectionId}
      />
    </TableLegacyTR>
  );
};
